/**
 * Iconos de navegacion, dibujados a mano en SVG.
 * Una libreria de iconos completa por cinco simbolos serian 30 kB de mas en
 * el paquete, y RNF-02 pide primera carga util en menos de 2,5 s en 4G.
 */

type IconProps = { className?: string };

const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

export const IconAgora = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <path d="M4 10 12 4l8 6" />
    <path d="M6 10v9M18 10v9M10 10v9M14 10v9" />
    <path d="M3 20h18" />
  </svg>
);

export const IconTrophy = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <path d="M7 4h10v5a5 5 0 0 1-10 0z" />
    <path d="M7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3" />
    <path d="M12 14v4M9 20h6" />
  </svg>
);

export const IconRanking = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <path d="M4 20V12h4v8M10 20V6h4v14M16 20v-6h4v6" />
    <path d="M3 20h18" />
  </svg>
);

/** Tablon del parque: dos chapas colgadas, una delante de otra. */
export const IconForum = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <path d="M3 5h13v9H9l-4 3v-3H3z" />
    <path d="M19 9h2v9h-2v3l-3-3h-4" />
  </svg>
);

export const IconUser = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <circle cx="12" cy="8" r="4" />
    <path d="M5 20a7 7 0 0 1 14 0" />
  </svg>
);

export const IconShieldAdmin = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <path d="M12 3l7 3v6c0 5-3 8-7 10-4-2-7-5-7-10V6z" />
    <path d="M9 13l2 2 4-4" />
  </svg>
);

export const IconSettings = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4" />
  </svg>
);

export const IconSun = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
  </svg>
);

export const IconMoon = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />
  </svg>
);

export const IconGlobe = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" />
  </svg>
);

export const IconMenu = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

export const IconSearch = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <circle cx="11" cy="11" r="7" />
    <path d="m16.5 16.5 4 4" />
  </svg>
);

export const IconCheck = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <path d="m5 13 4 4L19 7" />
  </svg>
);

export const IconX = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const IconArrowUp = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
);

export const IconArrowDown = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <path d="M12 5v14M5 12l7 7 7-7" />
  </svg>
);

export const IconExternal = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <path d="M14 4h6v6M20 4l-9 9" />
    <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  </svg>
);

export const IconDownload = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <path d="M12 4v11M8 11l4 4 4-4" />
    <path d="M4 19h16" />
  </svg>
);

export const IconInstagram = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17" cy="7" r="1" fill="currentColor" />
  </svg>
);

export const IconTikTok = ({ className }: IconProps) => (
  <svg {...base} className={className}>
    <path d="M15 4c0 3 2 5 5 5" />
    <path d="M15 4v10.5a4.5 4.5 0 1 1-4.5-4.5" />
  </svg>
);
